// StageEditModal — narrow edit modal for a CRM `LeadStage` (#1080, updated by #1958 Phase 2).
//
// @aiInvariant only `displayName` is editable from the web UI;
//   `code`/`kind`/`sortOrder` are read-only. `taskTitle`/`defaultDueMinutes`
//   remain backend compatibility fields, not web-editable (#1956 Phase 2).
//   Empty `displayName` is rejected (min 1).
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type LeadStage, type UpdateLeadStageInput } from '@magimp/shared';
import { Button, EditorModal, Input } from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';
import { usePatchLeadStage } from '../queries.js';

export interface StageEditModalProps {
  open: boolean;
  stage: LeadStage | null;
  onClose: () => void;
  onUpdated?: (stage: LeadStage) => void;
}

export function StageEditModal({ open, stage, onClose, onUpdated }: StageEditModalProps) {
  const { t } = useTranslation(['stages', 'common']);
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !stage) return;
    setDisplayName(stage.displayName);
    setError(null);
  }, [open, stage]);

  const patchMut = usePatchLeadStage(stage?.code ?? '');

  const baseline = useMemo(
    () => ({
      displayName: stage?.displayName ?? '',
    }),
    [stage],
  );

  const dirty = displayName !== baseline.displayName;
  const canSubmit = !patchMut.isPending && displayName.trim().length > 0 && dirty;

  async function onSubmit(): Promise<void> {
    if (!stage) return;
    setError(null);
    const body: UpdateLeadStageInput = {};
    if (displayName !== baseline.displayName) body.displayName = displayName.trim();
    try {
      const updated = await patchMut.mutateAsync(body);
      onUpdated?.(updated);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <EditorModal
      open={open && stage != null}
      title={t('stages:form.editTitle')}
      dirty={Boolean(dirty)}
      busy={patchMut.isPending}
      onConfirmClose={onClose}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>{t('common:actions.cancel')}</Button>
          <Button
            kind="primary"
            disabled={!canSubmit}
            onClick={() => {
              void onSubmit();
            }}
            data-testid="stage-save-button"
          >
            {t('stages:form.save')}
          </Button>
        </div>
      }
    >
      {stage && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>{t('stages:form.code')}</span>
            <span
              data-testid="stage-code-readonly"
              style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--fg-1)' }}
            >
              {stage.code}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>{t('stages:form.kind')}</span>
            <span data-testid="stage-kind-readonly" style={{ fontSize: 13, color: 'var(--fg-1)' }}>
              {t(`stages:kind.${stage.kind}`)}
            </span>
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>
              {t('stages:form.displayName')}
            </span>
            <div data-testid="stage-display-name-input">
              <Input value={displayName} onChange={setDisplayName} maxLength={128} />
            </div>
          </label>
          {error && (
            <span
              role="alert"
              style={{ fontSize: 12, color: 'var(--status-error-fg)' }}
              data-testid="stage-form-error"
            >
              {error}
            </span>
          )}
        </div>
      )}
    </EditorModal>
  );
}
